'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { ExternalLinkIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { SparkleIcon } from '@/components/shell-icons'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { dbNewsPostToRow } from '@/features/dashboard/components/widgets/news-row'
import { useDashboardCommands } from '@/features/dashboard/hooks/use-dashboard-commands'
import {
  HOME_EASE,
  HOME_LIST,
  HOME_ROW,
  HomeHeaderLink,
  HomePanel,
} from '@/features/home/components/home-motion'
import { useRelativeTime } from '@/features/home/hooks/use-relative-time'
import { useNumberFormat } from '@/hooks/use-number-format'
import { cn } from '@/lib/utils'
import { useDbNewsHistory } from '@/service/db-news.hook'
import { useEconomicCalendarHistory } from '@/service/economic-calendar.hook'
import { EventVolatility } from '@/types/generated/public_api'

type NewsView = 'headlines' | 'calendar'

const HEADLINES = 6
const EVENTS = 6
const HOUR = 3_600_000
const CALENDAR_HORIZON_MS = 72 * HOUR

const VOLATILITY_BARS: Partial<Record<EventVolatility, number>> = {
  [EventVolatility.EVENT_VOLATILITY_LOW]: 1,
  [EventVolatility.EVENT_VOLATILITY_MEDIUM]: 2,
  [EventVolatility.EVENT_VOLATILITY_HIGH]: 3,
}

function Headlines() {
  const t = useTranslations('trade.home.news')
  const relative = useRelativeTime()
  const { startResearchPrompt } = useDashboardCommands()
  const { data, isPending } = useDbNewsHistory({ limit: HEADLINES })
  const rows = useMemo(
    () => (data?.posts ?? []).map((post) => ({ row: dbNewsPostToRow(post), coins: post.coins })),
    [data]
  )

  if (isPending)
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-12 w-full" />
        ))}
      </div>
    )
  if (rows.length === 0) return <p className="text-foreground/50 py-6 text-sm">{t('noNews')}</p>

  return (
    <motion.ul variants={HOME_LIST} initial="hidden" animate="show" className="flex flex-col">
      {rows.slice(0, HEADLINES).map(({ row, coins }) => (
        <motion.li
          key={row.rowId}
          variants={HOME_ROW}
          className="group border-foreground/5 relative border-b py-2.5 last:border-b-0"
        >
          <div className="flex items-start gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-foreground line-clamp-2 text-sm leading-5 tracking-tight">
                {row.headline}
              </p>
              <div className="text-foreground/40 flex items-center gap-2 text-[11px]">
                <span>{relative(row.timestampMs)}</span>
                {coins.slice(0, 3).map((coin) => (
                  <span
                    key={coin}
                    className="bg-foreground/5 flex items-center gap-1 rounded-xs px-1 py-px"
                  >
                    <CryptoIcon symbol={coin} className="size-3" />
                    {coin}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity duration-200 group-focus-within:opacity-100 group-hover:opacity-100">
              <button
                type="button"
                onClick={() =>
                  startResearchPrompt(
                    `Explain this headline and how it could move the market, simply: "${row.headline}"`
                  )
                }
                aria-label={t('askAi')}
                title={t('askAi')}
                className="text-shell-accent hover:bg-shell-accent/10 flex size-7 cursor-pointer items-center justify-center rounded-full transition-colors"
              >
                <SparkleIcon className="size-3.5" />
              </button>
              {row.link && (
                <a
                  href={row.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('openSource')}
                  title={t('openSource')}
                  className="text-foreground/50 hover:text-foreground hover:bg-foreground/5 flex size-7 items-center justify-center rounded-full transition-colors"
                >
                  <ExternalLinkIcon className="size-3.5" />
                </a>
              )}
            </div>
          </div>
        </motion.li>
      ))}
    </motion.ul>
  )
}

function Calendar() {
  const t = useTranslations('trade.home.news')
  const fmt = useNumberFormat()
  // Fixed for the mount, so the query key (and the request) doesn't change every render.
  const [range] = useState(() => {
    const from = Date.now() - HOUR
    return { from: String(from), to: String(from + CALENDAR_HORIZON_MS) }
  })
  const { data, isPending } = useEconomicCalendarHistory({ ...range, limit: 100 })
  const events = useMemo(
    () =>
      (data?.events ?? [])
        .filter(
          (event) =>
            (event.volatility ?? 0) >= EventVolatility.EVENT_VOLATILITY_MEDIUM &&
            Number(event.dateUtc) >= Date.now() - HOUR
        )
        .sort((a, b) => Number(a.dateUtc) - Number(b.dateUtc))
        .slice(0, EVENTS),
    [data]
  )

  if (isPending)
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    )
  if (events.length === 0) return <p className="text-foreground/50 py-6 text-sm">{t('noEvents')}</p>

  return (
    <motion.ul variants={HOME_LIST} initial="hidden" animate="show" className="flex flex-col">
      {events.map((event) => {
        const at = Number(event.dateUtc)
        const inMs = at - Date.now()
        const bars =
          VOLATILITY_BARS[event.volatility ?? EventVolatility.EVENT_VOLATILITY_UNSPECIFIED] ?? 0
        return (
          <motion.li
            key={event.eventDateId}
            variants={HOME_ROW}
            className="border-foreground/5 flex items-center gap-3 border-b py-2.5 last:border-b-0"
          >
            <span className="bg-foreground/5 text-foreground/70 w-10 shrink-0 rounded-xs py-0.5 text-center text-[11px] font-medium">
              {event.currencyCode || event.countryCode}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-foreground truncate text-sm tracking-tight">{event.name}</span>
              <span className="text-foreground/40 text-[11px]">
                {event.consensus !== undefined
                  ? t('consensus', {
                      value: fmt.number(event.consensus, { maximumFractionDigits: 2 }),
                    })
                  : event.previous !== undefined
                    ? t('previous', {
                        value: fmt.number(event.previous, { maximumFractionDigits: 2 }),
                      })
                    : ' '}
              </span>
            </span>
            <span className="flex shrink-0 flex-col items-end gap-1">
              <span className={cn('text-xs', inMs < 0 ? 'text-chart-mint' : 'text-foreground/70')}>
                {inMs < 0
                  ? t('live')
                  : inMs < HOUR
                    ? t('inMinutes', { count: Math.max(1, Math.round(inMs / 60_000)) })
                    : t('inHours', { count: Math.round(inMs / HOUR) })}
              </span>
              <span className="flex gap-0.5" aria-label={t('impact', { level: bars })}>
                {[1, 2, 3].map((level) => (
                  <span
                    key={level}
                    className={cn(
                      'h-2 w-1 rounded-full',
                      level <= bars
                        ? bars === 3
                          ? 'bg-chart-red'
                          : 'bg-ox-gold'
                        : 'bg-foreground/15'
                    )}
                  />
                ))}
              </span>
            </span>
          </motion.li>
        )
      })}
    </motion.ul>
  )
}

/**
 * What the market is talking about: the latest headlines (each one a question for the AI away)
 * and the high-impact economic events coming up in the next three days, with a countdown.
 */
export function HomeNews({ className }: { className?: string }) {
  const t = useTranslations('trade.home.news')
  const { addWidget } = useDashboardCommands()
  const [view, setView] = useState<NewsView>('headlines')

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          <HomeHeaderLink
            onClick={() => addWidget(view === 'headlines' ? 'newsfeed' : 'economic-calendar')}
          >
            {t('addToPage')}
          </HomeHeaderLink>
          <SegmentedControl
            layoutId="home-news-view"
            size="xs"
            value={view}
            onChange={setView}
            options={[
              { value: 'headlines', label: t('views.headlines') },
              { value: 'calendar', label: t('views.calendar') },
            ]}
          />
        </>
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={view}
          initial={{ opacity: 0, x: view === 'calendar' ? 16 : -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: view === 'calendar' ? -16 : 16 }}
          transition={{ duration: 0.22, ease: HOME_EASE }}
        >
          {view === 'headlines' ? <Headlines /> : <Calendar />}
        </motion.div>
      </AnimatePresence>
    </HomePanel>
  )
}
